import { Component } from '@theme/component';

/** Scoped gallery enhancement; native image links and video controls remain the fallback. */
class FastUTVGallery extends Component {
  #abort;
  #observer;
  #states = new WeakMap();
  #videos = [];
  #items = [];
  #dialog;
  #opener;
  #motion;
  #connection;
  #policyListeners = [];
  #filter = 'all';

  connectedCallback() { super.connectedCallback(); this.#initialize(); }
  updatedCallback() { super.updatedCallback(); this.#initialize(); }
  disconnectedCallback() { this.#teardown(); super.disconnectedCallback(); }

  #initialize() {
    this.#teardown();
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.#items = [...this.querySelectorAll('[data-gallery-item]')];
    this.#videos = [...this.querySelectorAll('video[data-gallery-video]')];
    this.#dialog = this.querySelector('dialog[data-gallery-dialog]');
    this.#motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#connection = navigator.connection;
    for (const target of [this.#motion, this.#connection]) {
      if (!target?.addEventListener) continue;
      const listener = () => this.#syncVideos();
      target.addEventListener('change', listener);
      this.#policyListeners.push(() => target.removeEventListener('change', listener));
    }
    this.addEventListener('click', this.#handleClick, { signal });
    document.addEventListener('visibilitychange', () => this.#syncVideos(), { signal });
    if (this.#dialog instanceof HTMLDialogElement) {
      this.#dialog.addEventListener('cancel', event => { event.preventDefault(); this.#closeDialog(); }, { signal });
      this.#dialog.addEventListener('close', () => this.#afterClose(), { signal });
      this.#dialog.addEventListener('keydown', this.#handleDialogKey, { signal });
      this.#dialog.addEventListener('click', event => {
        const box = this.#dialog.getBoundingClientRect();
        if (event.target === this.#dialog && (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom)) this.#closeDialog();
      }, { signal });
    }
    for (const video of this.#videos) {
      const previous = this.#states.get(video);
      const state = { visible: false, userPaused: previous?.userPaused || false, manual: previous?.manual || false, automaticPause: false, automaticPlay: false, pending: null };
      this.#states.set(video, state);
      // The controller, not an autoplay attribute, starts only visible videos.
      video.autoplay = false;
      video.defaultMuted = true;
      video.muted = true;
      video.playsInline = true;
      video.preload = 'none';
      video.addEventListener('play', () => {
        if (!state.automaticPlay) { state.manual = true; state.userPaused = false; }
        if (!video.muted) this.#muteOthers(video);
        this.#updateControls(video);
      }, { signal });
      video.addEventListener('pause', () => {
        if (state.automaticPause) state.automaticPause = false;
        else if (state.visible && !document.hidden && !this.#dialog?.open && !video.ended) state.userPaused = true;
        this.#updateControls(video);
      }, { signal });
      video.addEventListener('volumechange', () => {
        if (!video.muted && video.volume > 0) this.#muteOthers(video);
        this.#updateControls(video);
      }, { signal });
      video.addEventListener('playing', () => this.#updateControls(video), { signal });
      video.addEventListener('error', () => this.#updateControls(video), { signal });
      this.#updateControls(video);
    }
    if ('IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver(entries => {
        for (const entry of entries) {
          const video = entry.target;
          const state = this.#states.get(video);
          if (!state) continue;
          state.visible = entry.isIntersecting && entry.intersectionRatio >= 0.35;
          this.#syncVideo(video);
        }
      }, { threshold: [0, 0.35] });
      for (const video of this.#videos) this.#observer.observe(video);
    }
    // Without IntersectionObserver, leave native/manual playback usable, with no automatic playback.
    const buttons = [...this.querySelectorAll('[data-gallery-filter]')];
    if (!buttons.some(button => this.#filterValue(button) === this.#filter)) this.#filter = 'all';
    this.#applyFilter(this.#filter);
    for (const button of buttons) button.hidden = false;
    const filterBar = this.querySelector('[data-gallery-filters]');
    if (filterBar) filterBar.hidden = false;
    this.dataset.galleryReady = 'true';
  }

  #teardown() {
    this.#abort?.abort();
    this.#observer?.disconnect();
    this.#observer = undefined;
    for (const remove of this.#policyListeners) remove();
    this.#policyListeners = [];
    for (const video of this.#videos) { video.pause(); video.muted = true; }
    if (this.#dialog?.open) this.#dialog.close();
    this.#opener = undefined;
    delete this.dataset.galleryReady;
  }

  #filterValue(button) { return button.dataset.galleryFilter || button.value || 'all'; }

  #applyFilter(value) {
    this.#filter = value;
    let count = 0;
    for (const item of this.#items) {
      const visible = value === 'all' || item.dataset.category === value;
      item.hidden = !visible;
      if (visible) count++;
      else for (const video of item.querySelectorAll('video[data-gallery-video]')) this.#pause(video, true);
    }
    for (const button of this.querySelectorAll('[data-gallery-filter]')) button.setAttribute('aria-pressed', String(this.#filterValue(button) === value));
    const status = this.querySelector('[data-gallery-count]');
    if (status) status.textContent = (this.dataset.countTemplate || '{{ count }}').replace(/__COUNT__|\{\{\s*count\s*\}\}|\{count\}/g, String(count));
    this.#syncVideos();
  }

  #lowMotionOrData() { return this.#motion?.matches || this.#connection?.saveData === true; }

  #canPlay(video) {
    const state = this.#states.get(video);
    const box = video.getBoundingClientRect();
    const area = box.width * box.height;
    const visibleArea = Math.max(0, Math.min(box.right, innerWidth) - Math.max(box.left, 0)) * Math.max(0, Math.min(box.bottom, innerHeight) - Math.max(box.top, 0));
    const visible = area > 0 && visibleArea / area >= 0.35;
    return this.isConnected && !document.hidden && !this.#dialog?.open && !video.closest('[data-gallery-item]')?.hidden && !state.userPaused && (visible || (!this.#observer && state.manual)) && (!this.#lowMotionOrData() || state.manual);
  }

  #syncVideos() { for (const video of this.#videos) this.#syncVideo(video); }

  #syncVideo(video) {
    if (this.#canPlay(video)) this.#play(video, false);
    else this.#pause(video, true);
  }

  #play(video, manual) {
    const state = this.#states.get(video);
    if (!state) return;
    if (manual) { state.manual = true; state.userPaused = false; }
    if (!this.#canPlay(video) || !video.paused || state.pending) return;
    state.automaticPlay = !manual;
    // No eager load() or source fan-out: only the selected/visible video reaches play().
    const attempt = video.play();
    if (!attempt?.then) { state.automaticPlay = false; return; }
    state.pending = attempt;
    attempt.catch(() => {
      // Autoplay denial leaves native controls and the explicit play button available.
    }).finally(() => {
      if (state.pending !== attempt) return;
      state.pending = null;
      state.automaticPlay = false;
      if (!this.#canPlay(video)) this.#pause(video, true);
      this.#updateControls(video);
    });
  }

  #pause(video, remute = false) {
    const state = this.#states.get(video);
    if (!state) return;
    if (!video.paused) { state.automaticPause = true; video.pause(); }
    if (remute) video.muted = true;
    this.#updateControls(video);
  }

  #muteOthers(active) {
    for (const video of this.#videos) if (video !== active && !video.muted) { video.muted = true; this.#updateControls(video); }
  }

  #updateControls(video) {
    const wrapper = video.closest('[data-gallery-film]');
    if (!wrapper) return;
    const play = wrapper.querySelector('[data-gallery-play]');
    const sound = wrapper.querySelector('[data-gallery-sound]');
    if (play) {
      const label = video.paused ? this.dataset.playLabel : this.dataset.pauseLabel;
      if (label) { play.textContent = label; play.setAttribute('aria-label', label); }
      play.setAttribute('aria-pressed', String(!video.paused));
    }
    if (sound) {
      const audible = !video.muted && video.volume > 0;
      const label = audible ? this.dataset.soundOff : this.dataset.soundOn;
      if (label) { sound.textContent = label; sound.setAttribute('aria-label', label); }
      sound.setAttribute('aria-pressed', String(audible));
    }
  }

  #handleClick = event => {
    if (!(event.target instanceof Element)) return;
    const filter = event.target.closest('[data-gallery-filter]');
    if (filter && this.contains(filter)) { this.#applyFilter(this.#filterValue(filter)); return; }
    const link = event.target.closest('a[data-gallery-open]');
    if (link && this.contains(link) && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && this.#dialog instanceof HTMLDialogElement && typeof this.#dialog.showModal === 'function') {
      if (!this.#dialog.querySelector('[data-gallery-full]')) return;
      event.preventDefault(); this.#openImage(link); return;
    }
    if (event.target.closest('[data-gallery-close]')) { this.#closeDialog(); return; }
    const direction = event.target.closest('[data-gallery-prev], [data-gallery-next]');
    if (direction) { this.#stepImage(direction.hasAttribute('data-gallery-next') ? 1 : -1); return; }
    const control = event.target.closest('[data-gallery-play], [data-gallery-sound]');
    if (!control || !this.contains(control)) return;
    const video = control.closest('[data-gallery-film]')?.querySelector('video[data-gallery-video]');
    const state = this.#states.get(video);
    if (!state) return;
    if (control.hasAttribute('data-gallery-play')) {
      if (video.paused) this.#play(video, true);
      else { state.userPaused = true; this.#pause(video); }
    } else {
      if (video.muted || video.volume === 0) { this.#muteOthers(video); if (video.volume === 0) video.volume = 1; video.muted = false; }
      else video.muted = true;
      this.#updateControls(video);
    }
  };

  #openImage(link) {
    const image = this.#dialog.querySelector('[data-gallery-full]');
    if (!(image instanceof HTMLImageElement)) return;
    const title = this.#dialog.querySelector('[data-gallery-title]');
    const caption = this.#dialog.querySelector('[data-gallery-caption]');
    image.src = link.href;
    image.alt = link.querySelector('img')?.alt || link.dataset.title || '';
    if (title) title.textContent = link.dataset.title || image.alt;
    if (caption) { caption.textContent = link.dataset.caption || ''; caption.hidden = !caption.textContent; }
    this.#opener = link;
    if (!this.#dialog.open) this.#dialog.showModal();
    this.#syncVideos();
    this.#dialog.querySelector('[data-gallery-close]')?.focus({ preventScroll: true });
  }

  #closeDialog() { if (this.#dialog?.open) this.#dialog.close(); }

  #afterClose() {
    if (this.#opener?.isConnected && !this.#opener.closest('[data-gallery-item]')?.hidden) this.#opener.focus({ preventScroll: true });
    this.#syncVideos();
  }

  #stepImage(direction) {
    if (!this.#dialog?.open) return;
    const links = [...this.querySelectorAll('a[data-gallery-open]')].filter(link => !link.closest('[data-gallery-item]')?.hidden);
    const index = links.indexOf(this.#opener);
    if (index < 0 || links.length < 2) return;
    this.#openImage(links[(index + direction + links.length) % links.length]);
  }

  #handleDialogKey = event => {
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') { event.preventDefault(); this.#stepImage(event.key === 'ArrowRight' ? 1 : -1); return; }
    if (event.key !== 'Tab') return;
    const focusable = [...this.#dialog.querySelectorAll('a[href],button:not(:disabled),[tabindex]:not([tabindex="-1"])')].filter(el => el.getClientRects().length);
    const first = focusable[0], last = focusable.at(-1);
    if (!first) { event.preventDefault(); return; }
    if (event.shiftKey && (document.activeElement === first || !this.#dialog.contains(document.activeElement))) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && (document.activeElement === last || !this.#dialog.contains(document.activeElement))) { event.preventDefault(); first.focus(); }
  };
}

if (!customElements.get('fastutv-gallery')) customElements.define('fastutv-gallery', FastUTVGallery);
